import React, { useEffect, useMemo, useState } from "react";
import useAxios from "axios-hooks";
import { Separator, Spinner, Stack, Text } from "@fluentui/react";
import ContentSummariesTable from "../Components/ContentSummariesTable";
import ContentSummariesFilters from "../Components/ContentSummariesFilters";
import ContentSummariesActions from "../Components/ContentSummariesActions";
import ContentSummaryEditor from "../Components/ContentSummaryEditor";
import FeatureSummary from "../../../../Util/FeatureSummary";
import SuccessComponent from "../../../../Util/SuccessComponent";
import ErrorComponent from "../../../../Util/ErrorComponent";
import useAuth from "../../../../../auth/useAuth";
import { getErrorMessage, parseContentIds } from "../contentSummariesHelpers";

const ContentSummariesPage = () => {
    const { isAdmin } = useAuth();
    const [searchText, setSearchText] = useState("");
    const [contentIdsInput, setContentIdsInput] = useState("");
    const [maxItems, setMaxItems] = useState("100");
    const [onlyPublished, setOnlyPublished] = useState(true);
    const [onlyMissingSummary, setOnlyMissingSummary] = useState(false);
    const [excludedTypeSlugs, setExcludedTypeSlugs] = useState([]);
    const [forceRegenerate, setForceRegenerate] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [activeContentId, setActiveContentId] = useState(null);
    const [summaryDraft, setSummaryDraft] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showError, setShowError] = useState(false);

    const [{ data: summaries, loading }, loadSummaries] = useAxios(
        { url: "resources/content/admin/summaries", method: "get" },
        { manual: true }
    );
    const [{ loading: savingSummary }, saveSummary] = useAxios(
        { method: "put" },
        { manual: true }
    );
    const [{ loading: generatingSummaries }, generateSummaries] = useAxios(
        { url: "resources/content/admin/summaries/generate", method: "post" },
        { manual: true }
    );

    const parsedContentIds = useMemo(
        () => parseContentIds(contentIdsInput),
        [contentIdsInput]
    );

    const items = summaries ?? [];
    const activeContent = useMemo(
        () => items.find((item) => item.contentId === activeContentId) || null,
        [activeContentId, items]
    );

    useEffect(() => {
        handleLoad();
    }, []);

    useEffect(() => {
        if (items.length === 0) {
            setActiveContentId(null);
            setSummaryDraft("");
            return;
        }

        if (selectedItems.length > 0) {
            const selectedItem = selectedItems[0];
            setActiveContentId(selectedItem.contentId);
            setSummaryDraft(selectedItem.summary || "");
            return;
        }

        if (!activeContentId || !items.some((item) => item.contentId === activeContentId)) {
            setActiveContentId(items[0].contentId);
            setSummaryDraft(items[0].summary || "");
        }
    }, [items, selectedItems]);

    useEffect(() => {
        if (activeContent) {
            setSummaryDraft(activeContent.summary || "");
        }
    }, [activeContent]);

    async function handleLoad() {
        try {
            setShowError(false);
            await loadSummaries({
                params: {
                    searchText: searchText || undefined,
                    contentIdsCsv: contentIdsInput || undefined,
                    excludedTypeSlugsCsv: excludedTypeSlugs.length > 0 ? excludedTypeSlugs.join(",") : undefined,
                    onlyPublished,
                    onlyMissingSummary,
                    maxItems: Number.parseInt(maxItems, 10) || 100,
                },
            });
        } catch (error) {
            setErrorMessage(getErrorMessage(error, "Unable to load content summaries."));
            setShowError(true);
        }
    }

    async function handleSaveSummary() {
        if (!activeContent) {
            return;
        }

        try {
            const result = await saveSummary({
                url: `resources/content/admin/summaries/${activeContent.contentId}`,
                data: {
                    summary: summaryDraft,
                },
            });

            const updatedItem = result.data;
            const updatedItems = items.map((item) =>
                item.contentId === updatedItem.contentId ? updatedItem : item
            );

            setSelectedItems((current) =>
                current.map((item) =>
                    item.contentId === updatedItem.contentId ? updatedItem : item
                )
            );
            setActiveContentId(updatedItem.contentId);
            setSummaryDraft(updatedItem.summary || "");
            setSuccessMessage(`Saved summary for content ${updatedItem.contentId}.`);
            setShowSuccess(true);
            setShowError(false);

            await loadSummaries({
                params: {
                    searchText: searchText || undefined,
                    contentIdsCsv: contentIdsInput || undefined,
                    excludedTypeSlugsCsv: excludedTypeSlugs.length > 0 ? excludedTypeSlugs.join(",") : undefined,
                    onlyPublished,
                    onlyMissingSummary,
                    maxItems: Number.parseInt(maxItems, 10) || 100,
                },
            });

            return updatedItems;
        } catch (error) {
            setErrorMessage(getErrorMessage(error, "Unable to save the summary."));
            setShowError(true);
        }

        return null;
    }

    async function handleGenerateSummaries() {
        const targetIds =
            selectedItems.length > 0
                ? selectedItems.map((item) => item.contentId)
                : parsedContentIds;

        if (targetIds.length === 0 || !isAdmin) {
            return;
        }

        try {
            const result = await generateSummaries({
                data: {
                    contentIds: targetIds,
                    maxItems: targetIds.length,
                    forceRegenerate,
                },
            });

            const generationResult = result.data;
            setSuccessMessage(
                `AI updated ${generationResult.updatedCount} summaries, skipped ${generationResult.skippedExistingSummaryCount}, and had ${generationResult.failedCount} failures.`
            );
            setShowSuccess(true);
            setShowError(false);
            await handleLoad();
        } catch (error) {
            setErrorMessage(getErrorMessage(error, "Unable to generate summaries."));
            setShowError(true);
        }
    }

    const isBusy = loading || savingSummary || generatingSummaries;

    return (
        <Stack tokens={{ childrenGap: 16 }}>
            <Text variant="xLarge">Content Summaries</Text>
            <Text>
                Review existing summaries, load specific content by ID, run AI updates
                for selected content, and manually edit a single summary below.
            </Text>

            <SuccessComponent
                successMessage={successMessage}
                showSuccess={showSuccess}
                setShowSuccess={setShowSuccess}
            />
            <ErrorComponent
                errorMessage={errorMessage}
                showError={showError}
                setShowError={setShowError}
                isMultiline={true}
            />

            <FeatureSummary
                label="About Content Summaries"
                summary="Use this page from top to bottom. First, narrow the list with search, missing-summary, published, or pasted content IDs. Next, select one or more rows if you want AI to regenerate summaries for those exact items. If no rows are selected, the AI action uses the pasted IDs instead. The table and preview show whether you are looking at a saved summary from the content table or a placeholder summary generated from the content for review. Finally, use the editor at the bottom to manually review or update the saved summary for one content item. Content text is shown for reference only and is not edited here."
            />

            <ContentSummariesFilters
                searchText={searchText}
                setSearchText={setSearchText}
                maxItems={maxItems}
                setMaxItems={setMaxItems}
                onlyPublished={onlyPublished}
                setOnlyPublished={setOnlyPublished}
                onlyMissingSummary={onlyMissingSummary}
                setOnlyMissingSummary={setOnlyMissingSummary}
                excludedTypeSlugs={excludedTypeSlugs}
                setExcludedTypeSlugs={setExcludedTypeSlugs}
                contentIdsInput={contentIdsInput}
                setContentIdsInput={setContentIdsInput}
                onLoad={handleLoad}
                isBusy={isBusy}
            />

            <ContentSummariesActions
                itemCount={items.length}
                selectedCount={selectedItems.length}
                parsedContentIdsCount={parsedContentIds.length}
                forceRegenerate={forceRegenerate}
                setForceRegenerate={setForceRegenerate}
                isAdmin={isAdmin}
                isBusy={isBusy}
                onGenerateSummaries={handleGenerateSummaries}
            />

            {loading && items.length === 0 ? (
                <Spinner label="Loading content summaries..." />
            ) : (
                <ContentSummariesTable
                    items={items}
                    loading={loading}
                    onSelectionChange={(selection) => {
                        const nextSelection = selection || [];
                        setSelectedItems(nextSelection);
                        if (nextSelection.length > 0) {
                            setActiveContentId(nextSelection[0].contentId);
                        }
                    }}
                />
            )}

            <Separator />

            <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="large">Manual Summary Editor</Text>
                <ContentSummaryEditor
                    activeContent={activeContent}
                    summaryDraft={summaryDraft}
                    setSummaryDraft={setSummaryDraft}
                    onSaveSummary={handleSaveSummary}
                    isBusy={isBusy}
                />
            </Stack>
        </Stack>
    );
};

export default ContentSummariesPage;
